import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { authService } from '../services/authService';
import { supportService } from '../services/supportService';

export default function HelpSupport() {
  const [user, setUser] = useState(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  useEffect(() => {
    const loadUser = async () => {
      try {
        const userData = await authService.getCurrentUser();
        if (userData) setUser(userData);
      } catch {
        setUser(authService.getUser());
      }
    };
    loadUser();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    setSubmitStatus(null);
    try {
      await supportService.submitSupportRequest(message.trim());
      setMessage('');
      setSubmitStatus('success');
    } catch {
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto">
        {/* Quick links back to dashboards */}
        <div className="mb-8 p-4 bg-[#1B3A6B]/5 border border-[#1B3A6B]/20 rounded-lg">
          <p className="text-sm font-medium text-gray-700 mb-2">Quick links back to your dashboard</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/candidates" className="text-[#1B3A6B] font-medium hover:underline">Candidate Graph</Link>
            <span className="text-gray-400">|</span>
            <Link to="/targeted-outreach" className="text-[#1B3A6B] font-medium hover:underline">Targeted Outreach</Link>
            <span className="text-gray-400">|</span>
            <Link to="/performance" className="text-[#1B3A6B] font-medium hover:underline">Performance</Link>
            <span className="text-gray-400">|</span>
            <Link to="/admin" className="text-[#1B3A6B] font-medium hover:underline">Admin Console</Link>
          </div>
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">Help and Support</h1>
        <p className="text-gray-600 mb-8">
          This page explains how SignalCue syncs your data and how our scoring works. Use the links below to jump to any section. You can return to your dashboards at any time using the links above.
        </p>

        {/* Table of contents */}
        <nav className="mb-10 p-6 bg-gray-50 rounded-lg border border-gray-200" aria-label="Page contents">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Contents</h2>
          <ul className="space-y-2 text-[#1B3A6B]">
            <li><a href="#how-signalcue-works" className="hover:underline">How SignalCue works</a></li>
            <li><a href="#data-sync" className="hover:underline">Data synchronisation process</a></li>
            <li><a href="#data-sources" className="hover:underline">Where your data comes from</a></li>
            <li><a href="#canonical-model" className="hover:underline">How we clean and match your data</a></li>
            <li><a href="#fit-score" className="hover:underline">Understanding the Fit Score (Candidate Graph)</a></li>
            <li><a href="#signal-score" className="hover:underline">Understanding the Signal Score</a></li>
            <li><a href="#priority-score" className="hover:underline">Understanding the Priority Score (Targeted Outreach)</a></li>
            <li><a href="#operational-buckets" className="hover:underline">Call now, Warm, and Track buckets</a></li>
            <li><a href="#contact-support" className="hover:underline">Contact support</a></li>
          </ul>
        </nav>

        {/* Content sections */}
        <div className="space-y-10 prose prose-gray max-w-none">
          <section id="how-signalcue-works">
            <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-2">How SignalCue works</h2>
            <p>
              SignalCue is an intelligence layer that sits above your Applicant Tracking System (ATS). It answers three practical questions for recruiters: who to contact, why today is the right time, and how to reference warm relationships using your historical placements and compliant public evidence. The platform uses clear, repeatable scoring so that every recommendation can be explained and checked, rather than opaque predictions.
            </p>
          </section>

          <section id="data-sync">
            <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-2">Data synchronisation process</h2>
            <p>
              Scoring is not a single calculation. SignalCue runs a controlled pipeline so that data is clean, mapped, deduplicated, and enriched before any score is produced.
            </p>
            <p>
              The pipeline does the following. First, it ingests data from your ATS (and optionally from file uploads such as spreadsheets). Then it normalises fields such as names, companies, email addresses, and phone numbers so that they can be compared consistently. Next, it resolves entities: it finds and merges duplicate people and companies so that each person or company appears once in the system. After that, it computes derived facts such as placement history, links to hiring managers, and when you last had contact with someone. For candidates and hiring managers, it also resolves &quot;where are they now&quot; using an evidence-based approach and records how confident we are in that information. The system then detects and accepts external signals (such as job postings or leadership changes) using strict quality rules. For the Candidate Graph (who to contact), the pipeline computes the Fit Score for people and saves the results with full explainability. Targeted Outreach (daily prioritised lists using Signal Score and Priority Score) is planned for a later release.
            </p>
            <p>
              Your organisation can configure ATS integrations (for example iSmartRecruit or other supported platforms) through the Admin Console. Once connected, the platform supports scheduled and incremental synchronisation so that new and updated data is brought in regularly. You can review sync status and logs in the integration and admin areas of the application.
            </p>
          </section>

          <section id="data-sources">
            <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-2">Where your data comes from</h2>
            <p>
              The primary source of data is your ATS. SignalCue treats the ATS as the main system of record for candidates, hiring managers, companies, jobs, placements, and activity logs. This forms the foundation for relationship depth, warm-path mapping, and internal signals such as backfills.
            </p>
            <p>
              Where your recruitment firm has legacy placement or contact data in files (for example in OneDrive or SharePoint), SignalCue can ingest spreadsheets from approved storage. Each imported row keeps file-level provenance so you can trace where the data came from.
            </p>
            <p>
              For &quot;why now&quot; evidence, SignalCue uses only legitimate public sources. These include company careers pages, official team or news pages, investor relations pages, exchange filings, regulator or registry notices, and reputable news outlets. Community profiles and open knowledge sources may be used cautiously as directional evidence only and are treated as lower trust. The platform does not scrape or use sources that violate third-party terms. A Source Registry is maintained per organisation, and each source can be toggled between active and paused by administrators. No signal is generated from a paused source.
            </p>
          </section>

          <section id="canonical-model">
            <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-2">How we clean and match your data</h2>
            <p>
              Recruitment data in ATS systems is often fragmented, duplicated, and inconsistently structured. Building scoring directly on raw ATS tables would fail because the same person can appear multiple times with different spellings, transliterations, or identifiers. SignalCue therefore uses a canonical model: it reconciles all inputs into stable entities before any scoring runs.
            </p>
            <p>
              The system maintains canonical representations of candidates, companies, and hiring managers. It detects and resolves duplicates, normalises titles and names, and keeps evidence provenance for every merged entity. Where a merge is ambiguous, the platform prefers not to merge and allows data operators to intervene, so that relationship graphs are not corrupted. Confidence indicators are shown so you can see how certain the system is about a match.
            </p>
          </section>

          <section id="fit-score">
            <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-2">Understanding the Fit Score (Candidate Graph)</h2>
            <p>
              The Fit Score is a number from zero to one hundred that ranks candidates and hiring managers based on the evidence we have to support a high-quality outreach attempt. It combines relationship strength with practical readiness. The aim is to help recruiters avoid preparing for calls where there is no warm path, no valid contact method, or unclear role context, while making sure that older relationships are still valued and not treated as worthless.
            </p>
            <p>
              The Fit Score is made up of five factors, each with a capped range. Relationship Depth (up to 30 points) reflects whether your firm has proven placement history with the person and whether that history includes an identifiable hiring manager link. Contactability (up to 15 points) measures whether the recruiter can realistically reach the person today via verified email or phone and recent successful contact. Relevance to focus area (up to 25 points) ensures that a strong relationship is only prioritised if it aligns with the recruiter&apos;s assigned sectors, role families, and geographies. Recency of engagement (up to 15 points) reflects how recently your firm interacted with the person. Where-now certainty (up to 15 points) expresses how confident we are that the person&apos;s current employer and title are correct, based on evidence from ATS, official company pages, or other approved sources.
            </p>
            <p>
              The final Fit Score is the sum of these five factors, clamped between zero and one hundred. Every point is attributable to a factor with explicit evidence. In the Candidate Graph, you can see the top contributors and their values so you understand why one person is ranked above another. If we do not have strong evidence for someone&apos;s current role, we show &quot;Last known role&quot; instead of asserting a confident current role.
            </p>
          </section>

          <section id="signal-score">
            <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-2">Understanding the Signal Score</h2>
            <p>
              A signal is a fresh, verifiable reason to reach out. Examples include newly posted roles, leadership changes, funding announcements, measurable headcount shifts, or internal backfills detected from your ATS activity. Every signal is tied to a source and a timestamp and can be audited.
            </p>
            <p>
              A signal is only accepted if it meets strict quality rules: it must come from one high-trust source (such as investor relations, exchange filings, or official company pages), or be supported by two corroborating mid-trust sources (for example careers page plus reputable news). Signals from paused sources are discarded. Unaccepted signals do not affect the score.
            </p>
            <p>
              Signals are perishable. A job posting detected recently is a strong &quot;why now&quot; indicator; the same posting detected months ago is usually no longer actionable. SignalCue uses a grace window where the signal is at full strength, then applies a decay over time, and finally the signal expires after a set time-to-live. This keeps the daily list focused on timely opportunities. The Signal Score is a number from zero to one hundred that combines the signal type strength, source trust, freshness, and relevance to your assigned sectors and territories.
            </p>
          </section>

          <section id="priority-score">
            <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-2">Understanding the Priority Score (Targeted Outreach)</h2>
            <p>
              The Targeted Outreach view gives you a daily, ranked list of companies (and contacts) to focus on. The ranking reflects two things: warm relationships tend to convert better than cold outreach, and timing matters because fresh evidence often indicates an immediate hiring window. SignalCue therefore combines relationship strength (Fit Score) with timing and intent evidence (Signal Score) to produce a Priority Score. This score drives the order of the list and how items are grouped into buckets.
            </p>
            <p>
              The Priority Score is a blend of sixty percent Fit Score and forty percent Signal Score, plus small bonuses (for example for internal backfill or prior placement at the same company) and small penalties (for example when there is no valid contact method). The sixty-forty weighting deliberately favours warm paths because they typically convert better. Every ranked row explains itself with the score breakdown, two or three plain-language reasons, and provenance links with trust and timestamps.
            </p>
          </section>

          <section id="operational-buckets">
            <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-2">Call now, Warm, and Track buckets</h2>
            <p>
              To support daily execution, SignalCue places list items into simple buckets. &quot;Call now&quot; is for items with Priority Score of 85 or above. &quot;Warm&quot; is for scores between 70 and 84. &quot;Track&quot; is for scores between 55 and 69. Items below 55 are suppressed unless enriched or overridden. This keeps the list manageable and actionable so recruiters know where to focus first.
            </p>
          </section>

          <section id="contact-support">
            <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-2">Contact support</h2>
            <p>
              If you have a question about data sync, scoring, or how to use SignalCue, you can send it using the form below. Your message will be sent to our support team. We will use your account details (such as user and organisation identifiers) to look up your setup and respond. Please do not include sensitive passwords or personal data in your message.
            </p>

            <form onSubmit={handleSubmit} className="mt-6 p-6 bg-gray-50 rounded-lg border border-gray-200 max-w-2xl">
              <label htmlFor="support-message" className="block text-sm font-medium text-gray-700 mb-2">
                Your question or message
              </label>
              <textarea
                id="support-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B3A6B] focus:border-[#1B3A6B]"
                placeholder="Type your question or describe what you need help with..."
                required
                disabled={submitting}
              />
              <div className="mt-4 flex items-center gap-4">
                <button
                  type="submit"
                  disabled={submitting || !message.trim()}
                  className="px-4 py-2 bg-[#1B3A6B] text-white font-medium rounded-lg hover:bg-[#1B3A6B]/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending...' : 'Send message'}
                </button>
                {submitStatus === 'success' && (
                  <span className="text-green-600 text-sm font-medium">Message sent. We will get back to you soon.</span>
                )}
                {submitStatus === 'error' && (
                  <span className="text-red-600 text-sm font-medium">Something went wrong. Please try again later.</span>
                )}
              </div>
            </form>
          </section>
        </div>

        {/* Back to dashboard link at bottom */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link to="/candidates" className="text-[#1B3A6B] font-medium hover:underline">
            ← Back to Candidate Graph
          </Link>
        </div>
      </div>
    </Layout>
  );
}
